/**
 * Topology page — live data-flow graph + canonical bus view.
 *
 * Two views switchable via tab (Map / Bus), persisted in the URL via
 * ``?view=``. Tag filter, refresh button and the side drawer for the
 * focused node are shared between both.
 *
 *   Map  — graph rendered with ReactFlow. Ingress on the left,
 *          canonical models in the centre, external sinks on the
 *          right. Edges = workflows, coloured by direction. Clicking
 *          a node focuses the subgraph reachable from it.
 *
 *   Bus  — canonical-centric list. Pick a canonical model from the
 *          sidebar, see its producers + consumers laid out as rows.
 *          Answers "what touches this canonical?" much more directly
 *          than the graph view does.
 */
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Boxes, Cog, GitBranch, Inbox, List, Network, RefreshCw, Send } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";

import { TagFilter, TagList } from "@/components/tags";
import { BusView } from "@/components/topology/BusView";
import { DIRECTION_COLOR, MapView } from "@/components/topology/MapView";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { topologyApi, type TopologyNode } from "@/lib/api-topology";
import { cn } from "@/lib/cn";


type ViewMode = "map" | "bus";


export function TopologyPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const view: ViewMode = params.get("view") === "bus" ? "bus" : "map";

  const [tagFilter, setTagFilter] = React.useState<string[]>([]);
  const [drawerNode, setDrawerNode] = React.useState<TopologyNode | null>(null);
  const [focusedNodeId, setFocusedNodeId] = React.useState<string | null>(null);

  const query = useQuery({
    queryKey: ["topology", tagFilter],
    queryFn: () =>
      topologyApi.get({ tag: tagFilter.length > 0 ? tagFilter : undefined }),
    // 10s tick so live edge animation (running workflows) + failure
    // badges stay current without hammering the backend.
    refetchInterval: 10_000,
  });

  function setView(next: ViewMode) {
    const p = new URLSearchParams(params);
    if (next === "map") p.delete("view");
    else p.set("view", next);
    setParams(p, { replace: true });
    // Clear focus when leaving the map (focus is map-only state).
    if (next !== "map") setFocusedNodeId(null);
  }

  const focusedEdges = React.useMemo(() => {
    if (!drawerNode) return [];
    return (query.data?.edges ?? []).filter(
      (e) => e.source === drawerNode.id || e.target === drawerNode.id,
    );
  }, [drawerNode, query.data]);

  return (
    <PageContainer>
      <PageHeader
        title={t("topology.title", "Topology")}
        description={
          view === "map"
            ? t("topology.subtitle.map", "Lane graph. Click a node to focus its subgraph.")
            : t("topology.subtitle.bus", "Per-canonical producers and consumers.")
        }
        actions={
          <div className="flex items-center gap-2">
            <ViewSwitch value={view} onChange={setView} />
            <TagFilter value={tagFilter} onChange={setTagFilter} />
            <Button
              size="sm"
              variant="outline"
              onClick={() => query.refetch()}
              disabled={query.isFetching}
            >
              <RefreshCw
                className={cn("h-3.5 w-3.5", query.isFetching && "animate-spin")}
                strokeWidth={1.5}
              />
              {t("common.refresh", "Refresh")}
            </Button>
          </div>
        }
      />
      <PageContent className="!p-0">
        {query.isLoading ? (
          <div className="grid h-full place-items-center text-sm text-muted-foreground">
            {t("common.loading")}
          </div>
        ) : query.data && query.data.nodes.length === 0 ? (
          <EmptyState
            icon={Network}
            title={t("topology.empty", "Nothing to draw yet")}
            description={t(
              "topology.emptyDescription",
              "Configure at least one ingress source + one active workflow to see the graph populate.",
            )}
          />
        ) : view === "map" ? (
          <div className="flex h-full">
            <div className="flex-1">
              <MapView
                nodes={query.data?.nodes ?? []}
                edges={query.data?.edges ?? []}
                focusedNodeId={focusedNodeId}
                onFocus={setFocusedNodeId}
                onOpenNode={setDrawerNode}
              />
            </div>
            <MapLegend />
          </div>
        ) : (
          <BusView data={query.data!} />
        )}
      </PageContent>

      <NodeDrawer
        node={drawerNode}
        focusedEdges={focusedEdges}
        onClose={() => setDrawerNode(null)}
      />
    </PageContainer>
  );
}


// ---------------------------------------------------------------------------
// View switch (segmented control)
// ---------------------------------------------------------------------------

function ViewSwitch({
  value,
  onChange,
}: {
  value: ViewMode;
  onChange: (next: ViewMode) => void;
}) {
  const { t } = useTranslation();
  const options: Array<{ value: ViewMode; label: string; icon: typeof GitBranch }> = [
    { value: "map", label: t("topology.view.map", "Map"), icon: GitBranch },
    { value: "bus", label: t("topology.view.bus", "Bus"), icon: List },
  ];
  return (
    <div className="inline-flex rounded-md border border-border bg-surface p-0.5">
      {options.map((o) => {
        const isActive = o.value === value;
        const Icon = o.icon;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={cn(
              "inline-flex h-7 items-center gap-1.5 rounded px-2.5 text-xs transition",
              isActive
                ? "bg-elevated text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
            {o.label}
          </button>
        );
      })}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Map legend (right rail, map view only)
// ---------------------------------------------------------------------------

function MapLegend() {
  const { t } = useTranslation();
  return (
    <aside className="hidden w-56 shrink-0 border-l border-border bg-surface p-3 text-xs lg:block">
      <div className="mb-2 text-[11px] uppercase tracking-wider text-muted-foreground">
        {t("topology.legend.nodes", "Nodes")}
      </div>
      <ul className="mb-4 space-y-1.5">
        <LegendRow icon={Inbox} label={t("topology.legend.ingress", "Ingress source")} />
        <LegendRow icon={Boxes} label={t("topology.legend.canonical", "Canonical model")} />
        <LegendRow icon={Send} label={t("topology.legend.sink", "External sink")} />
      </ul>
      <div className="mb-2 text-[11px] uppercase tracking-wider text-muted-foreground">
        {t("topology.legend.edges", "Workflow direction")}
      </div>
      <ul className="space-y-1.5">
        <LegendEdge color={DIRECTION_COLOR.inbound} label="inbound" />
        <LegendEdge color={DIRECTION_COLOR.internal} label="internal" />
        <LegendEdge color={DIRECTION_COLOR.outbound} label="outbound" />
      </ul>
      <div className="mb-2 mt-4 text-[11px] uppercase tracking-wider text-muted-foreground">
        {t("topology.legend.health", "Health (24h)")}
      </div>
      <ul className="space-y-1.5">
        <LegendEdge color="hsl(var(--danger))" label="last failed" />
        <li className="flex items-center gap-2">
          <span
            className="inline-block h-0.5 w-6 rounded"
            style={{
              background:
                "repeating-linear-gradient(to right, hsl(var(--muted-fg)) 0 3px, transparent 3px 6px)",
            }}
          />
          <span className="font-mono text-[11px]">no traffic</span>
        </li>
        <li className="flex items-center gap-2">
          <span className="inline-block h-1 w-6 rounded bg-success" />
          <span className="font-mono text-[11px]">thicker = more runs</span>
        </li>
      </ul>
      <p className="mt-4 text-[11px] leading-snug text-muted-foreground">
        {t(
          "topology.legend.hint",
          "Click a node to focus its subgraph + open the workflows drawer. Click background to clear.",
        )}
      </p>
    </aside>
  );
}

function LegendRow({ icon: Icon, label }: { icon: typeof Inbox; label: string }) {
  return (
    <li className="flex items-center gap-2">
      <Icon className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
      <span>{label}</span>
    </li>
  );
}

function LegendEdge({ color, label }: { color: string; label: string }) {
  return (
    <li className="flex items-center gap-2">
      <span
        className="inline-block h-0.5 w-6 rounded"
        style={{ backgroundColor: color }}
      />
      <span className="font-mono text-[11px]">{label}</span>
    </li>
  );
}


// ---------------------------------------------------------------------------
// Side drawer — opens on node click in either view
// ---------------------------------------------------------------------------

interface NodeDrawerProps {
  node: TopologyNode | null;
  focusedEdges: Array<{
    id: string;
    source: string;
    target: string;
    workflow_id: string;
    workflow_name: string;
    direction: "inbound" | "internal" | "outbound";
    enabled: boolean;
  }>;
  onClose: () => void;
}

function NodeDrawer({ node, focusedEdges, onClose }: NodeDrawerProps) {
  return (
    <Sheet open={!!node} onOpenChange={(o) => !o && onClose()}>
      <SheetContent>
        {node && (
          <>
            <SheetHeader>
              <SheetTitle>
                <span className="font-mono text-sm">{node.label}</span>
                <span className="ml-2 text-xs text-muted-foreground">
                  {node.sublabel}
                </span>
              </SheetTitle>
            </SheetHeader>
            <div className="space-y-4 p-2">
              {node.tags.length > 0 && (
                <div>
                  <div className="mb-1 text-[11px] uppercase tracking-wider text-muted-foreground">
                    Tags
                  </div>
                  <TagList tags={node.tags} />
                </div>
              )}

              {node.ref_id && node.type !== "external_sink" && (
                <div>
                  <DeepLink node={node} />
                </div>
              )}

              <div>
                <div className="mb-2 text-[11px] uppercase tracking-wider text-muted-foreground">
                  Workflows ({focusedEdges.length})
                </div>
                {focusedEdges.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    Nothing currently connected to this node.
                  </p>
                ) : (
                  <ul className="space-y-1">
                    {focusedEdges.map((e) => {
                      const isOutgoing = e.source === node.id;
                      return (
                        <li key={e.id}>
                          <Link
                            to={`/workflows/${e.workflow_id}`}
                            className="flex items-center justify-between gap-2 rounded-md border border-border bg-elevated px-2 py-1.5 hover:border-accent"
                          >
                            <span className="flex min-w-0 items-center gap-2">
                              <ArrowRight
                                className={cn(
                                  "h-3 w-3 shrink-0",
                                  isOutgoing ? "text-foreground" : "rotate-180 text-muted-foreground",
                                )}
                                strokeWidth={1.5}
                              />
                              <span className="truncate text-xs">{e.workflow_name}</span>
                            </span>
                            <Badge tone={e.enabled ? "active" : "neutral"}>
                              {e.direction}
                            </Badge>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function DeepLink({ node }: { node: TopologyNode }) {
  if (node.type === "ingress") {
    return (
      <Link
        to={`/ingress?focus=${node.ref_id}`}
        className="inline-flex items-center gap-1.5 text-xs text-accent hover:underline"
      >
        <Cog className="h-3 w-3" strokeWidth={1.5} />
        Open ingress source
      </Link>
    );
  }
  if (node.type === "canonical") {
    return (
      <Link
        to={`/canonical`}
        className="inline-flex items-center gap-1.5 text-xs text-accent hover:underline"
      >
        <Cog className="h-3 w-3" strokeWidth={1.5} />
        Open canonical models
      </Link>
    );
  }
  return null;
}
